import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { GlobalStyles } from "./globalstyles";
import "./styles/styles.css";
import "./App.css";
import "./styles/theme.css";
import "./styles/auth.css";
import Header from "./routes/navbar/navbar";
import Discover from "./routes/discover/discover";
import MyBookingsPage from "./routes/myBookingsPage/myBookingsPage";
import SignInPage from "./routes/signinPage/signinPage";
import ListShopPage from "./routes/listshop/listshopPage";
import RegisterPage from "./routes/registerPage/registerPage";
import ShopPage from "./routes/shopPage/shopPage";
import { CombinedContextProvider } from "./context/combinedContext";
import ProtectedRoute from "./routes/protectedRoutes/protectedRoutes";
import ViewBookingPage from "./routes/viewBooking/viewBooking";
import Footer from "./routes/footer/footer";
import About from "./routes/footer/about/about";
import Privacy from "./routes/footer/privacyPolicy/privacyPolicy";
import Terms from "./routes/footer/termsAndCondtions/termsAndCondtions";
import CalendarCard from "./routes/calendarCards/calendarCard";
import ProfileAdminCard from "./routes/profileAdminCard/profileAdminCard";
import RegisterShopAndAdminFlow from "./routes/registerShop/registerShop";
import { getInitialUserData } from "./context/allStates/userData";
import ConfirmCodePage from "./routes/confirmationCode/confirmationCode";
import AccountPage from "./routes/accountPage/accountPage";
import AccountBookings from "./routes/accountPage/accountBookings/accountBookings";
import AccountFavorites from "./routes/accountPage/accountFavorites/accountFavorites";
import AccountReviews from "./routes/accountPage/accountReviews/accountReviews";
import RegisterStaff from "./routes/registerStaff/registerStaff";
import DeleteShopConfirm from "./routes/deleteShopConfirm/deleteShopConfirm";
import ProfileStaffCard from "./routes/profileStaffCard/profileStaffCard";
import ReviewPage from "./routes/reviewPage/reviewPage";
import TeamPage from "./routes/teamPage/teamPage";
import ReportsPage from "./routes/reportsPage/reportsPage";
import StaffPage from "./routes/staffPage/staffPage";
import BillingPage from "./routes/billingPage/billingPage";
import PaddleInitializer from "./utils/paddleInitializer";
import ContactSupport from "./routes/footer/contactSupport/contactSupport";
import BusinessLanding from "./routes/businessLanding/businessLanding";
import HomeLanding from "./routes/homeLanding/homeLanding";
import "leaflet/dist/leaflet.css";
import DiscoverMapPage from "./routes/discoverMapPage/discoverMapPage";
import ForgotPassword from "./routes/forgotPassword/forgotPassword";
import ConfirmForgotPassword from "./routes/forgotPasswordConfirm/forgotPasswordConfirm";
import RefundPolicy from "./routes/footer/refundPolicy/refundPolicy";
import AccountNotifications from "./routes/accountPage/accountNotifications/accountNotifications";
import AccountBillingPage from "./routes/accountPage/accountBillingPage/accountBillingPage";
import AccountReferral from "./routes/accountPage/accountReferral/accountReferral";
import AccountLanguage from "./routes/accountPage/accountLanguage/accountLanguage";
import AccountSecurity from "./routes/accountPage/accountSecurity/accountSecurity";
import AccountCategories from "./routes/accountPage/accountCategories/accountCategories";
import CheckoutPage from "./routes/checkoutPage/checkoutPage";
import loadingImage from "./images/LogoFullText.png";
import AccountContact from "./routes/accountPage/accountContact/accountContact";
import AccountPrivacyPolicy from "./routes/accountPage/accountPrivacyPolicy/accountPrivacyPolicy";
import AccountTermsAndCondtions from "./routes/accountPage/accountTermsAndCondtions/accountTermsAndCondtions";
import AccountRefundPolicy from "./routes/accountPage/accountRefundPolicy/accountRefundPolicy";
import AboutPage from "./routes/accountPage/accountAboutPage/accountAboutPage";
import AccountHelpAndSupport from "./routes/accountPage/accountHelpAndSupport/accountHelpAndSupport";
import AccountFAQ from "./routes/accountPage/accountFAQ/accountFAQ";
import ReferralProgram from "./routes/referralProgram/referralProgram";
import NotFound from "./routes/common/notFound/notFound";

/* Absolute ceiling on the boot screen; the session restore budgets itself
   well below this. */
const BOOT_WATCHDOG_MS = 20000;

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
        document.getElementById('root')?.scrollTo(0, 0);
        document.querySelector('.app-container')?.scrollTo(0, 0);
    }, [pathname]);
    return null;
};
const App = () => {
	const [userData, setUserData] = useState(null);
	const [loadingUserData, setLoadingUserData] = useState(true);
	const [showLoader, setShowLoader] = useState(true);

	useEffect(() => {
		if (!loadingUserData) {
			// wait for fade animation to finish before removing loader
			const timer = setTimeout(() => {
				setShowLoader(false);
			}, 400); // match CSS duration

			return () => clearTimeout(timer);
		}
	}, [loadingUserData]);

	useEffect(() => {
		let cancelled = false;

		/* Last-resort backstop. getInitialUserData budgets its own time, so this
		   should never fire — but the whole app is behind this loader, and it is
		   never acceptable for it to stay up forever. */
		const watchdog = setTimeout(() => {
			if (cancelled) return;
			console.warn("Session restore exceeded its budget — starting the app anyway");
			setLoadingUserData(false);
		}, BOOT_WATCHDOG_MS);

		(async () => {
			try {
				const data = await getInitialUserData();
				if (!cancelled) setUserData(data);
			} catch (err) {
				console.warn("Failed to load user data:", err);
			} finally {
				if (!cancelled) setLoadingUserData(false);
				clearTimeout(watchdog);
			}
		})();

		return () => {
			cancelled = true;
			clearTimeout(watchdog);
		};
	}, []);

	useEffect(() => {
		document.documentElement.classList.remove("theme-dark", "theme-light");
		document.documentElement.classList.add("theme-light");
		document.body.classList.remove("theme-dark", "theme-light");
		document.body.classList.add("theme-light");
	}, []);


	// ⏳ Wait until userData is ready (fixes the redirect on refresh)
	if (loadingUserData) {
		return (
			<div
				style={{
					height: "100vh",
					width: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: "var(--bg)",
				}}
			>
				<img
					src={loadingImage}
					style={{
						height: "50px",
						// width: "300px",
					}}
					alt="Loading"
				/>
			</div>
		);
	}

	return (
		<>
			{showLoader && (
				<div className={`app-loader ${!loadingUserData ? "fade-out" : ""}`}>
					<img src={loadingImage} className="app-loader-logo" alt="Loading" />
				</div>
			)}

			<BrowserRouter>
            <ScrollToTop />
				<GlobalStyles />
				<CombinedContextProvider initialUserData={userData} isLoadingUser={loadingUserData}>
					<PaddleInitializer />
					<div className="app-container">
						<Header />
						<Routes>
							<Route
								path="/:mainCategory/:subCategory?"
								element={
									<ProtectedRoute allowedRoles={["customer", "guest"]}>
										<Discover />
									</ProtectedRoute>
								}
							/>

							<Route
								path="/map"
								element={
									<ProtectedRoute allowedRoles={["customer", "guest"]}>
										<DiscoverMapPage />
									</ProtectedRoute>
								}
							/>

							<Route
								path="/billing"
								element={
									<ProtectedRoute allowedRoles={["admin", "owner"]}>
										<BillingPage />
									</ProtectedRoute>
								}
							/>

							<Route
								path="/profile"
								element={
									<ProtectedRoute allowedRoles={["admin", "owner"]}>
										<ProfileAdminCard />
									</ProtectedRoute>
								}
							/>

							<Route
								path="/team"
								element={
									<ProtectedRoute allowedRoles={["admin", "owner"]}>
										<TeamPage />
									</ProtectedRoute>
								}
							/>

							{/* Staff are allowed in: the API scopes them to their own
							    bookings and clients rather than the whole shop. */}
							<Route
								path="/reports"
								element={
									<ProtectedRoute allowedRoles={["admin", "owner", "staff"]}>
										<ReportsPage />
									</ProtectedRoute>
								}
							/>

							<Route
								path="/calendar"
								element={
									<ProtectedRoute allowedRoles={["staff", "admin", "owner"]}>
										<CalendarCard />
									</ProtectedRoute>
								}
							/>

							<Route
								path="/listyourshop/:ref?"
								element={
									<ProtectedRoute allowedRoles={["guest", "customer"]}>
										<ListShopPage />
									</ProtectedRoute>
								}
							/>

							<Route
								path="/businesslanding"
								element={
									<ProtectedRoute allowedRoles={["guest", "customer"]}>
										<BusinessLanding />
									</ProtectedRoute>
								}
							/>

							<Route
								path="/"
								element={
									<ProtectedRoute allowedRoles={["guest", "customer"]}>
										<HomeLanding />
									</ProtectedRoute>
								}
							/>

							<Route
								path="/signin"
								element={
									<ProtectedRoute allowedRoles={["guest"]}>
										<SignInPage />{" "}
									</ProtectedRoute>
								}
							/>
							<Route
								path="/register"
								element={
									<ProtectedRoute allowedRoles={["guest"]}>
										<RegisterPage />
									</ProtectedRoute>
								}
							/>

							<Route
								path="/forgot-password"
								element={
									<ProtectedRoute allowedRoles={["guest"]}>
										<ForgotPassword />
									</ProtectedRoute>
								}
							/>

							<Route
								path="/confirmforgotpassword"
								element={
									<ProtectedRoute allowedRoles={["guest"]}>
										<ConfirmForgotPassword />
									</ProtectedRoute>
								}
							/>

							<Route
								path="/forgot-password"
								element={
									<ProtectedRoute allowedRoles={["guest"]}>
										<RegisterPage />
									</ProtectedRoute>
								}
							/>

							<Route
								path="/account"
								element={
									<ProtectedRoute allowedRoles={["customer", "staff", "admin", "owner"]}>
										<AccountPage />
									</ProtectedRoute>
								}
							>
								<Route path="bookings" element={<AccountBookings />} />
								<Route path="favorites" element={<AccountFavorites />} />
								<Route path="reviews" element={<AccountReviews />} />
								{/* <Route path="notifications" element={<AccountNotifications />} /> */}
								<Route path="billing" element={<AccountBillingPage />} />
								<Route path="referral" element={<AccountReferral />} />
								<Route path="category" element={<AccountCategories />} />
								<Route path="contact" element={<AccountContact />} />
							<Route path="faq" element={<AccountFAQ />} />
								<Route path="privacy" element={<AccountPrivacyPolicy />} />
								<Route path="terms" element={<AccountTermsAndCondtions />} />
								<Route path="refund" element={<AccountRefundPolicy />} />
								<Route path="about" element={<AboutPage />} />
								<Route path="helpandsupport" element={<AccountHelpAndSupport />} />


								<Route
									path="notifications"
									element={
										<ProtectedRoute allowedRoles={["staff", "owner", "admin", "customer"]}>
											<AccountNotifications />
										</ProtectedRoute>
									}
								/>
								<Route path="language" element={<AccountLanguage />} />
								<Route path="security" element={<AccountSecurity />} />
							</Route>
							<Route
								path="/staff/profile"
								element={
									<ProtectedRoute allowedRoles={["staff"]}>
										<ProfileStaffCard />
									</ProtectedRoute>
								}
							/>

							<Route
								path="/shops/:shopId/staff/:staffId"
								element={
									<ProtectedRoute allowedRoles={["guest", "customer"]}>
										<StaffPage />
									</ProtectedRoute>
								}
							/>

							<Route
								path="/checkout"
								element={
									<ProtectedRoute allowedRoles={["owner"]}>
										<CheckoutPage />
									</ProtectedRoute>
								}
							/>

							<Route path="/register/shop" element={<RegisterShopAndAdminFlow />} />
							<Route path="/register/staff" element={<RegisterStaff />} />
							<Route path="/account/delete/confirm" element={<DeleteShopConfirm />} />
							<Route path="/shops/:shopId" element={<ShopPage />} />
							{/* <Route path="/shops/:shopId/staff/:staffId" element={<ShopPage />} /> */}
							<Route path="/booking/:bookingId" element={<ViewBookingPage />} />
							<Route path="/reviews/:bookingId" element={<ReviewPage />} />
							<Route path="/about" element={<About />} />
							<Route path="/referral-program" element={<ReferralProgram />} />
							<Route path="/contact" element={<ContactSupport />} />
							<Route path="/privacy" element={<Privacy />} />
							<Route path="/refund" element={<RefundPolicy />} />
							<Route path="/terms" element={<Terms />} />
							<Route path="*" element={<NotFound />} />
						</Routes>
					</div>
					<Footer />
				</CombinedContextProvider>
			</BrowserRouter>
		</>
	);
};

export default App;
